import { CampaignStatus, PostStatus, PostTextAction, PostTextStatus, SocialCampaignDTO } from 'models/socialCampaign'
import { FC, KeyboardEvent, ReactNode, useEffect, useId, useRef, useState } from 'react'
import {
  PostDraft,
  PostEditability,
  blankDraft,
  changesOf,
  draftOfPost,
  editorModeOf,
  formatCount,
  isBlankDraft,
  isEmptyChange,
  neighboursOf,
  offersTextActions,
  postBlockerOf,
  postEditabilityOf,
  postTextStatusOf,
  reviewStateOf,
  scheduledOnceSaved,
  textLimitOf,
  todayIn
} from 'utils/socialCampaign/postEditor'
import { PostEditorFab, PostEditorFootActions, PostEditorPager, postEditorActionsOf } from './PostEditorActions.component'
import { PHONE_QUERY, useMatchMedia } from './useMatchMedia'
import { PostEditorOutcome, usePostEditor } from './usePostEditor'
import { STATUS_BADGE, badgeStatusOf } from './CampaignPostRow.component'
import { SwipeDirection, useEditorSwipe } from './useEditorSwipe'

import { CampaignConfirmDialog } from './CampaignConfirmDialog.component'
import { CheckOutlined } from '@mui/icons-material'
import { MUIBadge } from 'components/common/MUIBadge'
import Modal from 'components/common/Modals/Modal/Modal'
import { PostEditorAlert } from './PostEditorAlert.component'
import { PostEditorForm } from './PostEditorForm.component'
import { PostPreview } from './PostPreview.component'
import { PostTextActions } from './PostTextActions.component'
import { textActionsOf } from 'utils/socialCampaign/postText'
import { PostVisualsPicker } from './PostVisualsPicker.component'
import { PreviewAccounts } from './usePreviewAccounts'
import { SocialCampaignTarget } from 'dataQueries/socialCampaign.query'
import { TabButton } from 'components/common/TabButton'
import Typography from '@mui/material/Typography'
import classnames from 'classnames'
import { postDisplayStatus } from 'utils/socialCampaign/campaignStatus'
import { postSlotOf } from 'utils/socialCampaign/schedule'
import styles from './CampaignPostEditor.module.sass'
import { useTranslation } from 'react-i18next'

interface Props {
  campaign: SocialCampaignDTO
  target: SocialCampaignTarget
  now: Date
  editor: ReturnType<typeof usePostEditor>
  /** Who posts on each network, for the preview (`usePreviewAccounts`) */
  previewAccounts?: PreviewAccounts
}

/** A post written by hand takes everything; it is not saved until scheduled. */
const CREATING: PostEditability = { content: true, schedule: true, removable: false, needsNewTime: false, locked: null }

type Pane = 'edit' | 'preview'

/** Where a key press walks the posts: never from a field, a list or a set of tabs, which use the arrows themselves */
const KEEPS_ARROWS = 'input, textarea, select, video, [contenteditable="true"], [role="tab"], [role="tablist"], [role="listbox"], [role="option"]'

/** The visuals picker and the confirmations (removal, discarding a new post) paint above the editor */
const OVER_EDITOR = 110

/** A confirmation gives the focus back to the element that had it: the editor's box when the focus was outside it */
const keepFocusInEditor = (body: HTMLElement | null) => {
  const box = body?.closest<HTMLElement>('[role="dialog"]')
  if (box && !box.contains(document.activeElement)) box.focus()
}

/** An AI action answered on the post shown (ISSUE-3619) */
interface TextAnswer {
  action: PostTextAction
  /** The text it wrote */
  text: string
  /** The text it replaced, which Undo puts back: known when this editor asked it, dropped once the text changes again */
  before: string | null
  /** Undo put the text back */
  restored: boolean
}

/**
 * The post editor (ISSUE-3624, prototype v3 `renderEditor`, v2 decisions 1, 12 and 20): a large accessible dialog with
 * an address (`#post=…`, usePostEditor), in three modes on one layout. Review (a draft): "Post 2 / 7" walks every post,
 * the foot says how many drafts are left, removes, takes an approval back and approves then walks on; the last draft to
 * decide launches the campaign. Edit (scheduled, held, waiting or not published): Remove and "Save changes" (or Done).
 * Create (by hand): "Schedule the post", disabled with its reason until the post is complete.
 *
 * From 600px: two columns, the form and the preview (ISSUE-3650). Phone: a tall sheet, Edit / Preview tabs, the
 * foot walks the posts and the post's actions float above it in the dark bar; a horizontal swipe walks too
 * (useEditorSwipe). The arrows walk the posts from anywhere but a field or tabs.
 *
 * The text's AI actions (ISSUE-3619): the form's changes are saved, then the post's text is PENDING until the campaign
 * query reads it READY (the form takes the new text, keeping whatever else it holds) or FAILED (kept, Try again). While
 * PENDING the text and the networks wait: the backend writes the answer only while they are as they were asked (#625's
 * `textVersion`, with the subject); approving, undoing, the time and the visuals change nothing it reads. The backend
 * keeps no earlier text, so the editor keeps the one an action it asked replaced: Undo saves it back, until the text
 * changes again or the editor leaves the post.
 */
export const CampaignPostEditor: FC<Props> = ({ campaign, target, now, editor, previewAccounts }) => {
  const { t, i18n } = useTranslation(['social_campaign'])
  const id = `campaign-editor-${useId().replace(/:/g, '')}`
  const bodyRef = useRef<HTMLDivElement>(null)
  const primaryRef = useRef<HTMLButtonElement>(null)
  const { post } = editor
  const tz = campaign.timezone
  const mode = editorModeOf(post)
  const key = post?.id ?? 'new'

  // ---------------------------------------------------------------- the form
  const initialDraft = (): PostDraft => post ? draftOfPost(post, tz, campaign.networks) : blankDraft(campaign, editor.slot.day, editor.slot.time)
  const [draft, setDraft] = useState<PostDraft>(initialDraft)
  // Leaving without the editor's own buttons (Back, another tab of the property): what changed is still sent, once the
  // editor is gone; an action that closed it (saved, launched, removed, discarded) already settled the post, and one
  // still on its way sends the change itself
  const settled = useRef(false)
  const acting = useRef(false)
  const gone = useRef(false)
  // the post and form of the last commit, written after each commit: StrictMode runs a render twice
  const leaving = useRef({ post, draft, mode, savable: true, saveOnLeave: editor.saveOnLeave })
  const [pane, setPane] = useState<Pane>('edit')
  const [draftKey, setDraftKey] = useState(key)
  const ownMove = useRef(false)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [confirmingRemove, setConfirmingRemove] = useState(false)
  const [confirmingDiscard, setConfirmingDiscard] = useState(false)
  // a change that cannot be saved held the editor back: its reason shows with "Discard changes"
  const [heldBack, setHeldBack] = useState(false)
  // the text an AI action is asked on, once saved: what its answer replaces
  const asked = useRef<{ postId: string, text: string } | null>(null)
  const [answered, setAnswered] = useState<TextAnswer | null>(null)
  if (draftKey !== key) {
    // another post: its own form, on the Edit tab
    setDraftKey(key)
    setDraft(initialDraft())
    setPane('edit')
    setHeldBack(false)
    setAnswered(null)
    asked.current = null
  }

  // The backend's version of the same post (after a save, or a change made elsewhere) replaces a form that holds
  // nothing of its own
  const { networks } = campaign
  const previous = useRef(post)
  useEffect(() => {
    const before = previous.current
    previous.current = post
    if (!post || !before || before.id !== post.id || before === post) return
    setDraft((current) => isEmptyChange(changesOf(current, before, tz, networks)) || isEmptyChange(changesOf(current, post, tz, networks)) ? draftOfPost(post, tz, networks) : current)

    // an AI action answered: what it wrote is said, and what it replaced is known when this editor asked it
    const status = postTextStatusOf(post)
    if (status === PostTextStatus.PENDING) {
      setAnswered(null)
    } else if (postTextStatusOf(before) === PostTextStatus.PENDING) {
      // the text was held while rewritten, the time and the visuals were not: a form holding those takes the text alone,
      // or its next save would write the old text over the answer
      setDraft((current) => ({ ...current, text: post.text }))
      const replaced = asked.current?.postId === post.id ? asked.current.text : null
      asked.current = null
      setAnswered(status === PostTextStatus.READY && post.textAction
        ? { action: post.textAction, text: post.text, before: replaced !== post.text ? replaced : null, restored: false }
        : null)
    }
  }, [networks, post, tz])

  const change = (next: Partial<PostDraft>) => {
    setHeldBack(false)
    editor.clearError()
    setDraft((current) => ({ ...current, ...next }))
    // a text of the client's own: what the AI replaced is not theirs to get back any more, and the line saying what the
    // action did (or that Undo put the text back) no longer describes the field
    if (next.text !== undefined) setAnswered(null)
  }
  /** A change that cannot be saved stops the action: its reason shows above the form, on the Edit tab */
  const holdBack = () => {
    setHeldBack(true)
    setPane('edit')
  }
  const discard = () => {
    setHeldBack(false)
    editor.clearError()
    setDraft(initialDraft())
  }

  // ------------------------------------------------------------- the rules
  const editability = post ? postEditabilityOf(post, campaign, now) : CREATING
  const changes = post ? changesOf(draft, post, tz, campaign.networks) : null
  const dirty = mode === 'create' || (!!changes && !isEmptyChange(changes))
  const blocker = postBlockerOf(draft, {
    mode,
    changes,
    needsNewTime: editability.needsNewTime,
    goesOut: mode === 'create' || (!!post && scheduledOnceSaved(post, campaign, now)),
    timezone: tz,
    editFreezeMinutes: campaign.editFreezeMinutes,
    now,
    saved: post ? postSlotOf(post, tz) : null,
  })
  const blockerText = blocker && t(`editor.blocker.${blocker}`, {
    minutes: campaign.editFreezeMinutes,
    limit: formatCount(textLimitOf(draft.networks).limit, i18n.resolvedLanguage || i18n.language),
  })
  const review = post && mode === 'review' ? reviewStateOf(post, campaign.posts) : null
  const nav = post ? neighboursOf(campaign.posts, post.id) : null
  const draftCampaign = campaign.status === CampaignStatus.DRAFT
  const busy = editor.pending
  const waiting = post?.status === PostStatus.WAITING_FOR_CONTENT
  // An AI action rewrites the text (ISSUE-3619): its answer is written only while the text and the networks are as
  // they were asked, so those two wait; everything else stays open
  const textStatus = post ? postTextStatusOf(post) : PostTextStatus.READY
  const rewriting = textStatus === PostTextStatus.PENDING
  // they also hold still while the action is asked (its save, then the request): a change typed then would miss the
  // text the AI rewrites; and while Undo saves the text back, which would replace one typed meanwhile
  const [asking, setAsking] = useState(false)
  const [restoring, setRestoring] = useState(false)
  const textHeld = rewriting || asking || restoring
  // Undo: the text the last action replaced, while the text is still the one it wrote, in the form and saved
  const undoTo = answered && textStatus === PostTextStatus.READY && post?.text === answered.text && draft.text === answered.text ? answered.before : null
  const restored = !!answered?.restored
  // Undo goes with the text it put back: the focus goes to that text
  useEffect(() => {
    if (restored) bodyRef.current?.querySelector<HTMLTextAreaElement>('[data-swipe-pane="edit"] textarea:not([aria-hidden])')?.focus()
  }, [restored])

  // ----------------------------------------------------------- the actions
  const directionTo = (to: string): SwipeDirection =>
    campaign.posts.findIndex((item) => item.id === to) > (nav?.index ?? -1) ? 'next' : 'prev'

  const swipe = useEditorSwipe({
    boxRef: bodyRef,
    enabled: mode !== 'create' && !!nav && nav.index !== -1,
    previousId: nav?.previous?.id ?? null,
    nextId: nav?.next?.id ?? null,
    pane,
    postId: post?.id ?? null,
    onSwipe: (to, direction) => void walkTo(to, direction),
  })

  useEffect(() => {
    gone.current = false
    return () => {
      gone.current = true
      const { post: left, draft: unsaved, mode: leftMode, savable, saveOnLeave } = leaving.current
      if (settled.current || acting.current || leftMode === 'create' || !left) return
      saveOnLeave(left, unsaved, savable)
    }
  }, [])
  // Left through the address (browser Forward, a hand-edited #post=), not the editor's own walk: what changed is still
  // sent. Runs before `leaving` takes this commit's post
  useEffect(() => {
    const { post: left, draft: unsaved, mode: leftMode, savable, saveOnLeave } = leaving.current
    if ((left?.id ?? 'new') === key) return
    const own = ownMove.current
    ownMove.current = false
    if (own || acting.current || settled.current || leftMode === 'create' || !left) return
    saveOnLeave(left, unsaved, savable)
  }, [key])
  useEffect(() => {
    leaving.current = { post, draft, mode, savable: !blocker, saveOnLeave: editor.saveOnLeave }
  })
  const settle = (outcome: PostEditorOutcome) => {
    if (outcome.kind === 'closed' || outcome.kind === 'launched') settled.current = true
    return outcome
  }
  /**
   * Runs an action of the editor; null once the editor went meanwhile (Back): its answer must not walk or reopen it, and
   * a change it could not save (refused, or held back and not sent: `unsent`) is said by the page.
   */
  async function run(action: () => Promise<PostEditorOutcome>, unsent = false): Promise<PostEditorOutcome | null> {
    acting.current = true
    let outcome: PostEditorOutcome
    try {
      outcome = await action()
    } finally {
      acting.current = false
    }
    if (!gone.current) return outcome
    if (outcome.kind === 'stay') editor.leftUnsaved(post?.id ?? 'new', 'actionOnLeave')
    else if (unsent) editor.leftUnsaved(post?.id ?? 'new', 'heldBackOnLeave')
    return null
  }

  /** The editor's own move to another post: the action saved what changed */
  const goTo = (to: string) => {
    ownMove.current = true
    editor.goTo(to)
  }


  /** Leaves for another post: what changed is saved first; a change that cannot be saved holds the walk back. */
  async function walkTo(to: string, direction: SwipeDirection) {
    if (!post || busy) return swipe.reset()
    if (dirty && blocker) {
      holdBack()
      return swipe.reset()
    }
    const outcome = await run(() => editor.walk(post, draft, to))
    if (!outcome) return
    if (outcome.kind === 'walk') swipe.slide(direction, () => goTo(outcome.to))
    else swipe.reset()
  }

  async function approve() {
    if (!post || busy) return
    if (dirty && blocker) return holdBack()
    const outcome = await run(() => editor.approvePost(post, draft))
    if (!outcome) return
    settle(outcome)
    if (outcome.kind === 'walk') swipe.slide(directionTo(outcome.to), () => goTo(outcome.to))
  }

  async function launch() {
    if (!post || busy) return
    if (dirty && blocker) return holdBack()
    const outcome = await run(() => editor.launch(post, draft))
    if (outcome) settle(outcome)
  }

  async function undoApproval() {
    if (!post || busy) return
    // what was typed goes first, as with approve; a change that cannot be saved stays in the form
    const typed = dirty && !blocker ? draft : null
    if (await run(() => editor.undoApproval(post, typed), dirty && !typed)) primaryRef.current?.focus()
  }

  async function removeConfirmed() {
    setConfirmingRemove(false)
    if (!post || busy) return
    const outcome = await run(() => editor.removePost(post, mode === 'review'))
    if (!outcome) return
    settle(outcome)
    if (outcome.kind === 'walk') goTo(outcome.to)
  }

  async function create() {
    if (busy || blocker) return
    const outcome = await run(() => editor.createPost(draft))
    if (outcome) settle(outcome)
  }

  /** A post written by hand is not saved until scheduled: closing one that holds something asks first. */
  function closeNew() {
    if (isBlankDraft(draft, campaign)) return editor.discardAndClose(null)
    // a click on the scrim leaves the focus on the page: the confirmation must find the editor to give it back to
    keepFocusInEditor(bodyRef.current)
    setConfirmingDiscard(true)
  }

  // Back took the address of a new post that holds something: the hook keeps it on screen, the same question is asked
  const holdsSomething = mode === 'create' && !isBlankDraft(draft, campaign)
  const { reportNew, leftNew } = editor
  useEffect(() => {
    reportNew(holdsSomething)
    return () => reportNew(false)
  }, [holdsSomething, reportNew])
  useEffect(() => {
    if (!leftNew) return
    keepFocusInEditor(bodyRef.current)
    setConfirmingDiscard(true)
  }, [leftNew])

  function discardConfirmed() {
    setConfirmingDiscard(false)
    editor.discardAndClose(null)
  }

  function keepEditing() {
    setConfirmingDiscard(false)
    editor.keepNew()
  }

  /** Done, Save changes, the close button, Escape, the scrim: what changed is saved first. */
  async function close() {
    if (busy) return
    if (mode === 'create') return closeNew()
    if (dirty && blocker) return holdBack()
    const outcome = await run(() => editor.closeEditor(post, draft))
    if (outcome) settle(outcome)
  }

  /** Regenerate, Shorter, More hashtags: what changed is saved first (the AI rewrites the saved text). */
  async function rewriteText(action: PostTextAction) {
    if (!post || busy || rewriting) return
    if (dirty && blocker) return holdBack()
    asked.current = { postId: post.id, text: draft.text }
    setAsking(true)
    let outcome: PostEditorOutcome | null = null
    try {
      // in run() like the other actions: Back during the save must not send the change a second time
      outcome = await run(() => editor.requestText(post, draft, action))
    } finally {
      setAsking(false)
      if (outcome?.kind !== 'asked') asked.current = null
    }
  }

  /** Undo after an AI action: the text it replaced is saved back, as a typed text is (with what else changed). */
  async function undoText() {
    if (!post || busy || undoTo === null) return
    if (dirty && blocker) return holdBack()
    const text = undoTo
    setRestoring(true)
    let outcome: PostEditorOutcome | null = null
    try {
      outcome = await run(() => editor.restoreText(post, draft, text))
    } finally {
      setRestoring(false)
    }
    // refused: the form keeps the text the backend holds, and Undo stays
    if (outcome?.kind !== 'restored') return
    // set here: a form holding other changes is not replaced by the backend's version
    setDraft((current) => ({ ...current, text }))
    setAnswered((current) => current && { ...current, before: null, restored: true })
  }

  // The Modal's document listener, only while the editor is the top dialog; it replaces the Modal's own Escape
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      return void close()
    }
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
    if ((event.target as HTMLElement).closest(KEEPS_ARROWS) || !nav) return
    const to = event.key === 'ArrowLeft' ? nav.previous : nav.next
    if (!to) return
    event.preventDefault()
    void walkTo(to.id, event.key === 'ArrowLeft' ? 'prev' : 'next')
  }

  // ------------------------------------------------------------ the head
  const subject = mode === 'create' ? t('editor.new_title') : post?.subject || t('editor.untitled')
  let badge: ReactNode = null
  if (post && mode === 'review') {
    badge = post.reviewed
      ? <MUIBadge component="span" size="md" color="green" icon={<CheckOutlined />} label={t('editor.approved')} />
      : <MUIBadge component="span" size="md" color={STATUS_BADGE.DRAFT.color} icon={STATUS_BADGE.DRAFT.icon} label={t('schedule.status.DRAFT')} />
  } else if (post) {
    const status = badgeStatusOf(postDisplayStatus(post, campaign, now))
    badge = <MUIBadge component="span" size="md" color={STATUS_BADGE[status].color} icon={STATUS_BADGE[status].icon} label={t(`schedule.status.${status}`)} />
  }
  const position = nav && nav.index !== -1 ? t('editor.position', { index: nav.index + 1, total: nav.total }) : null
  const description = !post
    ? t('editor.create_description')
    : [
      t(`schedule.role.${post.role}`),
      waiting ? t('schedule.status.WAITING') : t('schedule.visuals', { count: draft.media.length }),
      position,
    ].filter(Boolean).join(' · ')

  // ------------------------------------------------------------ the foot
  const actions = postEditorActionsOf({
    post,
    review,
    hasNext: !!nav?.next,
    draftCampaign,
    removable: editability.removable,
    dirty,
    blockerText,
    t,
    on: {
      approve: () => void approve(),
      launch: () => void launch(),
      next: () => nav?.next && void walkTo(nav.next.id, 'next'),
      close: () => void close(),
      create: () => void create(),
    },
  })
  // a post written by hand keeps its one action in the foot on a phone too
  const hasFab = !!post
  // the tabs show on a phone only: on a wider screen both panes sit side by side, no tabpanel to name
  const isPhone = useMatchMedia(PHONE_QUERY)
  const paneOf = (item: Pane) => isPhone ? { role: 'tabpanel', 'aria-labelledby': `${id}-tab-${item}` } : {}
  const onRemove = () => setConfirmingRemove(true)
  const onUndo = () => void undoApproval()

  const otherDrafts = post ? campaign.posts.filter((item) => item.status === PostStatus.DRAFT && item.id !== post.id).length : 0
  const removeTitle = review?.removeLaunches ? t(draftCampaign ? 'editor.remove_confirm.title_launch' : 'editor.remove_confirm.title_schedule') : t('editor.remove_confirm.title')
  const removeBody = [
    t('editor.remove_confirm.body', { subject: post?.subject || t('editor.untitled') }),
    review?.removeLaunches ? t(draftCampaign ? 'editor.remove_confirm.body_launch' : 'editor.remove_confirm.body_schedule', { count: otherDrafts }) : null,
  ].filter(Boolean).join(' ')

  return (
    <>
      <Modal
        isOpen
        isAccessibleDialog
        size="lg"
        isSheetOnPhone
        hasHeaderContentDivider
        modalContentClassName={classnames(styles.editor, { [styles.withFab]: hasFab })}
        scrollableContentClassName={styles.scroll}
        // the badge sits in the title's line (prototype `mc3-ed__titlerow`): a hidden comma keeps the dialog's name readable
        title={
          <Typography component="h2" variant="inherit" className={styles.heading} data-swipe-part="">
            <span className={styles.subject}>{subject}</span>
            {badge && <span className={styles.srOnly}>, </span>}
            {badge}
          </Typography>
        }
        subtitle={<span className={styles.description} data-swipe-part="">{description}</span>}
        closeLabel={t('editor.close')}
        onClose={() => void close()}
        onClickOutside={() => void close()}
        onKeyDown={onKeyDown}
        footerStart={nav && nav.index !== -1 && mode !== 'create' &&
          <PostEditorPager
            index={nav.index}
            total={nav.total}
            hasPrevious={!!nav.previous}
            hasNext={!!nav.next}
            busy={busy}
            onPrevious={() => nav.previous && void walkTo(nav.previous.id, 'prev')}
            onNext={() => nav.next && void walkTo(nav.next.id, 'next')}
          />
        }
        footerContent={<PostEditorFootActions ref={primaryRef} model={actions} busy={busy} reasonId={`${id}-reason`} onRemove={onRemove} onUndo={onUndo} />}
      >
        <div ref={bodyRef} className={styles.body} data-swipe-body="" data-pane={pane}>

          <div
            className={styles.tabs}
            role="tablist"
            aria-label={t('editor.tabs_label')}
            data-swipe-tabs=""
            onKeyDown={(event) => {
              if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
              event.preventDefault()
              const next: Pane = pane === 'edit' ? 'preview' : 'edit'
              setPane(next)
              document.getElementById(`${id}-tab-${next}`)?.focus()
            }}
          >
            {(['edit', 'preview'] as const).map((item) => (
              <TabButton
                key={item}
                isTab
                id={`${id}-tab-${item}`}
                ariaControls={`${id}-pane-${item}`}
                tabIndex={pane === item ? 0 : -1}
                isActive={pane === item}
                label={t(`editor.tab_${item}`)}
                onClick={() => setPane(item)}
              />
            ))}
          </div>

          <div className={styles.form} id={`${id}-pane-edit`} {...paneOf('edit')} data-swipe-pane="edit">

            <PostEditorAlert error={editor.error} heldBack={heldBack ? blockerText : null} onDiscard={dirty && mode !== 'create' ? discard : undefined} />

            <PostEditorForm
              draft={draft}
              onChange={change}
              editability={editability}
              isTextHeld={textHeld}
              isTextBusy={rewriting}
              campaignNetworks={campaign.networks}
              waiting={waiting}
              minDay={todayIn(tz, now)}
              editFreezeMinutes={campaign.editFreezeMinutes}
              onOpenPicker={() => setPickerOpen(true)}
              textActions={post && offersTextActions(mode, editability, waiting) &&
                <PostTextActions
                  status={textStatus}
                  lastAction={post.textAction ?? null}
                  done={answered && textStatus === PostTextStatus.READY ? answered.action : null}
                  restored={restored}
                  disabled={busy}
                  available={textActionsOf(draft.text, post.role, draft.networks)}
                  // the failure is about the text it was asked on: once the client typed, the text is theirs
                  showFailure={textStatus === PostTextStatus.FAILED && draft.text === post.text}
                  onRun={(action) => void rewriteText(action)}
                  onUndo={undoTo !== null ? () => void undoText() : undefined}
                />
              }
            />

          </div>

          <div className={styles.preview} id={`${id}-pane-preview`} {...paneOf('preview')} data-swipe-pane="preview">
            {/* keyed: each post opens on its first network's preview (prototype `ed.preview = null`) */}
            <PostPreview key={key} networks={draft.networks} text={draft.text} media={draft.media} waiting={waiting} accounts={previewAccounts} subject={post?.subject} />
          </div>

          {hasFab && <PostEditorFab model={actions} busy={busy} onRemove={onRemove} onUndo={onUndo} />}

        </div>
      </Modal>

      {pickerOpen &&
        <PostVisualsPicker
          target={target}
          picked={draft.media}
          zIndex={OVER_EDITOR}
          onCancel={() => setPickerOpen(false)}
          onUse={(media) => {
            setPickerOpen(false)
            change({ media })
          }}
        />
      }

      <CampaignConfirmDialog
        open={confirmingRemove}
        title={removeTitle}
        body={removeBody}
        confirmLabel={review?.removeLaunches ? t(draftCampaign ? 'editor.remove_launch' : 'editor.remove_schedule') : t('editor.remove')}
        cancelLabel={t('editor.remove_confirm.keep')}
        destructive
        zIndex={OVER_EDITOR}
        onConfirm={() => void removeConfirmed()}
        onCancel={() => setConfirmingRemove(false)}
      />

      <CampaignConfirmDialog
        open={confirmingDiscard}
        title={t('editor.discard_confirm.title')}
        body={t('editor.discard_confirm.body')}
        confirmLabel={t('editor.discard_confirm.discard')}
        cancelLabel={t('editor.discard_confirm.keep')}
        destructive
        zIndex={OVER_EDITOR}
        onConfirm={discardConfirmed}
        onCancel={keepEditing}
      />
    </>
  )
}
